import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Award,
  BarChart3,
  BookOpen,
  ChevronDown,
  GraduationCap,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Plus,
  Settings,
  ShieldCheck,
  Target,
  Trophy,
  User,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import anuKingdomLogo from '../assets/images/anu-kingdom-logo.webp';
import './Navbar.css';

const guestLinks = [{ to: '/courses', label: 'Courses', icon: BookOpen }];

const learnerLinks = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/my-courses', label: 'My learning', icon: GraduationCap },
  { to: '/courses', label: 'Courses', icon: BookOpen },
  { to: '/goals', label: 'Goals', icon: Target },
  { to: '/achievements', label: 'Achievements', icon: Award },
  { to: '/certificates', label: 'Certificates', icon: ShieldCheck },
  { to: '/badges', label: 'Badges', icon: Trophy },
  { to: '/leaderboard', label: 'Leaderboard', icon: Users },
];

const instructorLinks = [
  { to: '/instructor', label: 'Instructor console', icon: LayoutDashboard },
  { to: '/my-courses', label: 'My courses', icon: BookOpen },
  { to: '/create-course', label: 'Create course', icon: Plus },
];

const adminLinks = [
  { to: '/admin', label: 'Admin dashboard', icon: LayoutDashboard },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/create-course', label: 'Courses', icon: BookOpen },
  { to: '/students', label: 'Students', icon: Users },
  { to: '/instructor', label: 'Instructor console', icon: GraduationCap },
  { to: '/certificates', label: 'Certificates', icon: ShieldCheck },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const links = getLinksForUser(user);
  const primaryCount = user?.role === 'admin' || user?.role === 'sub-admin' ? 4 : 3;
  const primaryLinks = links.slice(0, primaryCount);
  const secondaryLinks = links.slice(primaryCount);
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const isAuthRoute = [
    '/login',
    '/admin/login',
    '/instructor/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/auth/callback',
  ].some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

  if (isAuthRoute) {
    return null;
  }

  return (
    <header className="lms-topbar">
      <div className="lms-nav-container">
        <Link to="/" className="lms-brand" aria-label="Anu Kingdom home">
          <img
            src={anuKingdomLogo}
            alt="Anu Kingdom"
          />
          <span className="lms-brand-copy">
            <strong>Anu Kingdom</strong>
            <small>Learning platform</small>
          </span>
        </Link>

        <button
          type="button"
          className="lms-menu-toggle"
          aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((current) => !current)}
        >
          {mobileOpen ? <X size={21} /> : <Menu size={21} />}
        </button>

        <div className={`lms-nav-content ${mobileOpen ? 'open' : ''}`}>
          <nav aria-label="Main navigation">
            <ul className="lms-nav-links">
              {primaryLinks.map((link) => (
                <NavItem key={link.to} link={link} />
              ))}
              {secondaryLinks.length > 0 && (
                <li>
                  <details className="lms-more-menu">
                    <summary>
                      More
                      <ChevronDown size={15} aria-hidden="true" />
                    </summary>
                    <div className="lms-dropdown">
                      {secondaryLinks.map((link) => (
                        <NavItem key={link.to} link={link} dropdown />
                      ))}
                    </div>
                  </details>
                </li>
              )}
            </ul>
          </nav>

          {user ? (
            <details className="lms-account-menu">
              <summary>
                <span className="lms-user-avatar">{initials || <User size={16} />}</span>
                <span className="lms-user-copy">
                  <strong>{user.first_name || 'Account'}</strong>
                  <small>{formatRole(user.role)}</small>
                </span>
                <ChevronDown size={15} aria-hidden="true" />
              </summary>
              <div className="lms-account-dropdown">
                <Link to="/profile">
                  <User size={16} aria-hidden="true" />
                  Profile
                </Link>
                <Link to="/settings">
                  <Settings size={16} aria-hidden="true" />
                  Settings
                </Link>
                <button type="button" onClick={handleLogout}>
                  <LogOut size={16} aria-hidden="true" />
                  Log out
                </button>
              </div>
            </details>
          ) : (
            <div className="lms-auth-actions">
              <Link to="/login" className="lms-login-link">
                <LogIn size={16} aria-hidden="true" />
                Log in
              </Link>
              <Link to="/register" className="lms-register-link">
                Create account
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const NavItem = ({ link, dropdown = false }) => {
  const Icon = link.icon;

  return (
    <li className={dropdown ? 'dropdown-item' : undefined}>
      <NavLink
        to={link.to}
        end={link.end}
        className={({ isActive }) => (isActive ? 'active' : undefined)}
      >
        <Icon size={16} aria-hidden="true" />
        {link.label}
      </NavLink>
    </li>
  );
};

const getLinksForUser = (user) => {
  if (!user) return guestLinks;
  if (user.role === 'admin' || user.role === 'sub-admin') return adminLinks;
  if (user.role === 'instructor') return instructorLinks;
  return learnerLinks;
};

const formatRole = (role = '') =>
  role
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export default Navbar;
